/**
 * Builds Firebase config from environment variables.
 * Set environment-specific variables for each deployment target (dev/stg/prd).
 *
 * Required for all environments: FIREBASE_API_KEY, FIREBASE_AUTH_DOMAIN, FIREBASE_PROJECT_ID,
 *                                FIREBASE_STORAGE_BUCKET, FIREBASE_APP_ID
 * Optional, depending on environment: FIREBASE_DATABASE_URL, FIREBASE_MESSAGING_SENDER_ID, FIREBASE_MEASUREMENT_ID
 *
 * Note: Uses direct process.env.FIREBASE_* so Nuxt/Webpack DefinePlugin can inline values at build time.
 * Dynamic access like process.env[key] is NOT replaced and stays undefined on the client.
 */
function str(v: string | undefined): string | undefined {
  return v === "" ? undefined : v;
}

export function getFirebaseConfig(): Record<string, string> {
  const apiKey = str(process.env.FIREBASE_API_KEY);
  const authDomain = str(process.env.FIREBASE_AUTH_DOMAIN);
  const projectId = str(process.env.FIREBASE_PROJECT_ID);
  const storageBucket = str(process.env.FIREBASE_STORAGE_BUCKET);
  const appId = str(process.env.FIREBASE_APP_ID);

  const missing: string[] = [];
  if (!apiKey) missing.push("FIREBASE_API_KEY");
  if (!authDomain) missing.push("FIREBASE_AUTH_DOMAIN");
  if (!projectId) missing.push("FIREBASE_PROJECT_ID");
  if (!storageBucket) missing.push("FIREBASE_STORAGE_BUCKET");
  if (!appId) missing.push("FIREBASE_APP_ID");

  if (missing.length > 0) {
    throw new Error(
      `Firebase config missing required env: ${missing.join(", ")}. ` +
        "Set them in .env or your deployment environment."
    );
  }

  const config: Record<string, string> = {
    apiKey: apiKey!,
    authDomain: authDomain!,
    projectId: projectId!,
    storageBucket: storageBucket!,
    appId: appId!,
  };

  const databaseURL = str(process.env.FIREBASE_DATABASE_URL);
  if (databaseURL) config.databaseURL = databaseURL;

  const messagingSenderId = str(process.env.FIREBASE_MESSAGING_SENDER_ID);
  if (messagingSenderId) config.messagingSenderId = messagingSenderId;

  const measurementId = str(process.env.FIREBASE_MEASUREMENT_ID);
  if (measurementId) config.measurementId = measurementId;

  return config;
}
